import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Printer, ArrowLeft } from "lucide-react";
import { api, formatRp, formatDate, statusMeta, getStatusDisplay } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function ReceiptPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [svc, setSvc] = useState(null);
  const [shop, setShop] = useState({ name: "Rizki Service", legal_name: "PT Rizki Teknologi Maju", address: "", phone: "" });

  useEffect(() => {
    Promise.all([api.get(`/services/${id}`), api.get("/shop")])
      .then(([s, sh]) => {
        setSvc(s.data);
        setShop(sh.data);
      })
      .catch(() => {});
  }, [id]);

  if (!svc) return <div className="p-10 text-center text-slate-500">Memuat...</div>;

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <div className="flex items-center justify-between no-print">
        <Button variant="ghost" size="sm" onClick={() => nav(-1)} className="gap-1">
          <ArrowLeft className="w-4 h-4" /> Kembali
        </Button>
        <Button
          onClick={() => window.print()}
          data-testid="print-btn"
          className="bg-slate-900 hover:bg-slate-800 gap-2"
        >
          <Printer className="w-4 h-4" /> Cetak / Download PDF
        </Button>
      </div>

      <div
        id="receipt-print"
        className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 sm:p-8"
      >
        <div className="flex items-start justify-between border-b-2 border-slate-900 pb-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="h-14 w-24 flex items-center justify-center shrink-0">
              <img src="/logo-rtm.png" alt="Rizki Service" className="h-full w-full object-contain" />
            </div>
            <div>
              <div className="font-display font-bold text-xl text-slate-900 leading-tight">{shop.name}</div>
              {shop.legal_name && (
                <div className="text-sm text-slate-700 font-semibold leading-tight">{shop.legal_name}</div>
              )}
              <div className="text-xs text-slate-600 mt-1">{shop.address}</div>
              <div className="text-xs text-slate-600">Telp: {shop.phone}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
              Tanda Terima Servis
            </div>
            <div className="font-mono font-bold text-slate-900 mt-1">{svc.ticket_no}</div>
            <div className="text-[11px] text-slate-500">{formatDate(svc.created_at)}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              Pelanggan
            </div>
            <div className="mt-0.5 font-medium text-slate-900">{svc.customer_name}</div>
            <div className="text-slate-600 text-xs">{svc.customer_phone}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              Status
            </div>
            <div className="mt-0.5 font-medium text-slate-900">
              {getStatusDisplay(svc).label}
            </div>
            {svc.assigned_tech_name && (
              <div className="text-slate-600 text-xs">Teknisi: {svc.assigned_tech_name}</div>
            )}
          </div>
        </div>

        <div className="border border-slate-200 rounded-lg overflow-hidden mb-5">
          <table className="w-full text-sm">
            <tbody className="divide-y divide-slate-100">
              <tr>
                <td className="px-4 py-2 bg-slate-50 text-slate-500 text-xs uppercase tracking-wider w-1/3">
                  Jenis Barang
                </td>
                <td className="px-4 py-2 text-slate-900">{svc.item_type}</td>
              </tr>
              <tr>
                <td className="px-4 py-2 bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                  Merek / Model
                </td>
                <td className="px-4 py-2 text-slate-900">{svc.item_brand}</td>
              </tr>
              <tr>
                <td className="px-4 py-2 bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                  Keluhan
                </td>
                <td className="px-4 py-2 text-slate-900 whitespace-pre-line">{svc.complaint}</td>
              </tr>
              {svc.diagnosis && (
                <tr>
                  <td className="px-4 py-2 bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                    Diagnosa
                  </td>
                  <td className="px-4 py-2 text-slate-900 whitespace-pre-line">{svc.diagnosis}</td>
                </tr>
              )}
              {svc.shipping_address && (
                <tr>
                  <td className="px-4 py-2 bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                    Alamat Pengiriman
                  </td>
                  <td className="px-4 py-2 text-slate-900 text-sm">
                    <div className="font-medium">{svc.shipping_address.label}{svc.shipping_address.recipient ? ` · ${svc.shipping_address.recipient}` : ""}</div>
                    <div className="whitespace-pre-line">{svc.shipping_address.full_address}</div>
                    <div className="text-xs text-slate-500">{[svc.shipping_address.city, svc.shipping_address.postal_code].filter(Boolean).join(" · ")}</div>
                  </td>
                </tr>
              )}
              <tr>
                <td className="px-4 py-2 bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                  Estimasi Biaya
                </td>
                <td className="px-4 py-2 text-slate-900">{formatRp(svc.estimated_cost)}</td>
              </tr>
              {svc.final_cost > 0 && (
                <tr>
                  <td className="px-4 py-2 bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                    Total Akhir
                  </td>
                  <td className="px-4 py-2 text-slate-900 font-bold">{formatRp(svc.final_cost)}</td>
                </tr>
              )}
              {svc.pickup_method && (
                <tr data-testid="receipt-pickup-row">
                  <td className="px-4 py-2 bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                    Pengambilan
                  </td>
                  <td className="px-4 py-2 text-slate-900 text-sm">
                    {svc.pickup_method === "delivery" ? (
                      <>
                        <span className="font-semibold">Kirim via {svc.courier || "-"}</span>
                        {svc.tracking_number && <div className="text-xs text-slate-600 font-mono">No. Resi: {svc.tracking_number}</div>}
                      </>
                    ) : (
                      <span className="font-semibold">Ambil Sendiri</span>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="text-xs text-slate-600 space-y-1 mb-6 p-3 bg-slate-50 rounded-lg">
          <div className="font-semibold text-slate-800">Syarat & Ketentuan:</div>
          <ul className="list-disc pl-4 space-y-0.5">
            <li>Barang yang tidak diambil dalam 30 hari bukan tanggung jawab kami.</li>
            <li>Estimasi biaya dapat berubah sesuai kondisi kerusakan setelah pengecekan.</li>
            <li>Klaim garansi wajib membawa tanda terima ini.</li>
            <li>Data pribadi pada perangkat menjadi tanggung jawab pemilik.</li>
          </ul>
        </div>

        <div className="grid grid-cols-2 gap-8 pt-6 mt-4">
          <div className="text-center">
            <div className="text-xs text-slate-500 mb-10">Pelanggan</div>
            <div className="border-t border-slate-400 pt-1 text-xs text-slate-700">
              ({svc.customer_name})
            </div>
          </div>
          <div className="text-center">
            <div className="text-xs text-slate-500 mb-10">Petugas</div>
            <div className="border-t border-slate-400 pt-1 text-xs text-slate-700">
              ({svc.assigned_tech_name || shop.name})
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
